/**
 * Excel formula locale utilities.
 *
 * Office.js exposes two formula properties on Range:
 *
 *   .formulas       — always uses English/international function names
 *                     (SUM, VLOOKUP, IF, COUNTIFS …)
 *   .formulasLocal  — uses the locale of the running Excel instance
 *                     (SOMME, RECHERCHEV, SI, NB.SI.ENS … for fr-FR)
 *
 * For reading:
 *   - Use formulasLocal when displaying formulas to the user.
 *   - Use formulas when sending to AI (better accuracy with English names).
 *
 * For writing:
 *   - Use formulasLocal when the formula uses localized function names
 *     (generated by AI in the user's language).
 *   - Use formulas when the formula uses English function names.
 *
 * Formula locale resolution:
 *   The user can choose 'auto', 'fr', or 'en' in settings.
 *   'auto' detects from Excel's display language. If the Excel locale
 *   cannot be detected (e.g. running in a browser outside Office),
 *   'auto' falls back to the response language setting.
 */

/* global Office */

import type { FormulaLocale } from '@excel-ai-studio/shared';
import type { FormulaLocaleMode } from '@excel-ai-studio/shared';
import { getSettings } from '../store/settings.store';

/**
 * Returns the Excel host display language (e.g. "fr-FR", "en-US").
 * Falls back to "en-US" when running outside Office.
 */
export function getExcelLocale(): string {
  try {
    return Office.context.displayLanguage ?? 'en-US';
  } catch {
    return 'en-US';
  }
}

/**
 * Whether the current Excel locale uses non-English function names.
 * When true, we should read with formulasLocal and write generated
 * formulas with formulasLocal.
 */
export function isFrenchLocale(): boolean {
  return getExcelLocale().startsWith('fr');
}

/**
 * Resolve the effective formula locale mode ('fr' or 'en') from settings.
 *
 *   'fr' / 'en'  → return as-is
 *   'auto'       → detect from Excel's displayLanguage
 *                   if detection fails, fall back to settings.language
 *
 * This is the single source of truth used by FormulaPage, FormulaCard,
 * and any other code that needs to decide between French / English
 * formula handling.
 */
export function getEffectiveFormulaLocale(settingOverride?: FormulaLocale): FormulaLocaleMode {
  const formulaLocale = settingOverride ?? getSettings().formulaLocale;

  if (formulaLocale === 'fr') return 'fr';
  if (formulaLocale === 'en') return 'en';

  // 'auto' — detect from Excel locale
  if (isFrenchLocale()) return 'fr';

  // Outside Office or non-French Excel — use the response language setting
  const lang = getSettings().language;
  return lang === 'fr' ? 'fr' : 'en';
}
